// =============================================================================
// ui/tabs/Calendar.tsx — the month grid of daily P&L.
// -----------------------------------------------------------------------------
// The subtlest bounded-read case ported so far, and the one most likely to be
// believed.
//
// A month is a WINDOW, so a month inside the 45-day boot read is exactly right.
// But the calendar has arrows, and the moment a user pages back past the loaded
// window every day cell renders empty — which reads as **"I didn't trade in
// May"**, not as "May isn't loaded". Nothing is marked, nothing errors, and the
// screen is entirely plausible.
//
// So coverage is CHECKED BEFORE the grid is drawn — `coversWindow` — and an
// uncovered month renders its own state instead of thirty honest-looking zeros.
//
// ── 🚨 THREE THINGS THE FIRST VERSION GOT WRONG, ALL FIXED HERE ──
//
// 1. **It counted trades no other screen counts.** It bucketed every row in the
//    window with no account filter and no metrics gate, so SIM and replay
//    accounts landed in the cells. Every other analytics surface starts from
//    `tradesCountingForMetrics`; this one now does too. Same defect as the Day
//    Overview's average-day baseline, one tab over.
//
// 2. **The week ran Mon–Sun, seven columns.** Beta's runs SUN–FRI, six columns,
//    with the seventh cell holding the WEEK TOTAL. Saturday gets no column
//    because CME has no Saturday session — the space goes to something worth
//    reading. See `domain/calendar.ts`.
//
// 3. **Clicking a day did nothing.** In beta a day cell is the way into that
//    day's review, everywhere it appears. That is the whole point of a calendar
//    on a trading journal.
//
// ── 🚨 AND FOUR MORE THAT ONLY A SCREENSHOT COULD SHOW ──
//
// A winning day is drawn **BLUE**. `--green` in this theme is `#2e9dff` and its
// own definition says "semantic positive (BLUE in this theme)". The first grid
// hardcoded a literal green, so wins came out a colour nothing else in either
// app uses. The tiles were a second, centred visual language for the shared
// `.kpi` — which turned out to have no base rule at all. See `app.css`.
//
// ── WHAT IS HERE NOW ──
//
// Month view, year view (12 mini-calendars), streaks & records, the
// day-of-week breakdown and the year heatmap. That is beta's whole tab.
//
// ⚠ The three cards below the grid read ALL HISTORY, not the month on screen —
// except two rows inside Streaks, which say "this month" on their own labels.
// That mix is beta's and it is deliberate; a streak inside one month is not a
// streak. Do not "fix" it into consistency without reading the labels.
// =============================================================================

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useJournal } from '../state/journal';
import { useAccounts } from '../state/accounts';
import { useFilters } from '../state/filters';
import { useContracts } from '../state/contracts';
import { usePnlUnit } from '../state/usePnlUnit';
import type { PnlUnit } from '../../selectors/pnlUnit';
import { usePreferences } from '../state/preferences';
import { useSnapshot } from '../state/snapshot';
import { useShare } from '../state/share';
import { activeAccountLabel } from '../../selectors/share';
import { useSupabase } from '../state/supabase';
import { loadJournalDates } from '../../io/sync/journal';
import {
  coversWindow, effectiveTradeDate, isWholeBook, rowsForWindow,
} from '../../selectors/population';
import { tradesCountingForMetrics } from '../../selectors/accounts';
import {
  calendarDays,
  bestMonthOfYear, calendarKpis, dowBarPct, dowBreakdown, monthGrid, monthPrefix,
  monthRecords, monthStart, streaks, yearHeatmap, yearMonths,
  // ⚠ ONE threshold for both grids. Each view used to test its own copy — the
  // month a literal, the year the constant — which is how they came to disagree.
  HEAVY_DAY_USD,
  type CalendarByDate, type CalendarCell, type WeekSummary,
} from '../../selectors/calendar';
// ⚠ Beta heads all three bottom cards with `ICONS.analytics`; this is the same
// glyph, extracted from the old client by script rather than transcribed.
import { Icon } from '../shell/Icon';
import { pnlClass, unitCompact, unitValue } from '../format';

/** Beta's header: six days from Sunday, then the week column. */
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const monthLabel = (start: string): string =>
  new Date(`${start}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });

/**
 * ⚠ BETA'S ID (app/index.html:30488), so a picture saved from either client is
 * a picture of the same region.
 */
const CALENDAR_SNAPSHOT_REGION_ID = 'calendar-snapshot-region';

export interface CalendarTabProps {
  /** Opens the Day Overview on a date. Wired by the app shell. */
  onOpenDay?: (date: string) => void;
}

export function CalendarTab({ onOpenDay }: CalendarTabProps = {}) {
  const { phase, trades, today, loadWholeBook } = useJournal();
  const { accounts } = useAccounts();
  const { accounts: selection } = useFilters();
  const { contracts } = useContracts();
  // This tab converts fully — the day totals are now summed in the unit by
  // `calendarDays`, not reformatted after the fact (#44).
  const { unit } = usePnlUnit();
  const { sb } = useSupabase();
  // ⚠ The mode decides WHICH DAY a trade lands on. Bucketing with a guessed mode
  // puts evening trades in the wrong cell — and a calendar that is one day out
  // is entirely plausible, which is the failure this whole rebuild keeps closing.
  const { prefs } = usePreferences();
  const mode = prefs?.dayBoundaryMode ?? null;
  const snapshot = useSnapshot();
  const { setSource } = useShare();
  const [offset, setOffset] = useState(0);
  const [view, setView] = useState<'month' | 'year'>('month');

  // ⚠ A SET OF DATES, not the journal. One boolean per cell does not justify
  // paging every entry and every blob on it. See `loadJournalDates`.
  const [journalled, setJournalled] = useState<Set<string> | null>(null);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const dates = await loadJournalDates(sb);
        if (alive) setJournalled(dates);
      } catch {
        // ⚠ Swallowed ON PURPOSE, and this is the one place it is right: the
        // dot is decoration. A failed read means no dots, and the numbers on
        // the same screen are unaffected — so an error banner here would claim
        // the calendar is wrong when it is complete.
        if (alive) setJournalled(new Set());
      }
    })();
    return () => { alive = false; };
  }, [sb]);

  const start = monthStart(today, offset);
  // ⚠ 🚨 THE YEAR VIEW NEEDS ITS OWN COVERAGE CHECK, AND IT IS NOT THE MONTH'S.
  //
  // The month view refuses to draw an uncovered month because thirty empty cells
  // read as a quiet month. The year view has the SAME failure twelve times over
  // and worse: with only the 45-day boot read loaded, eight mini-calendars print
  // "no activity" — a sentence, not an absence — and the four tiles total a
  // fraction of the year under a label that says the whole of it.
  //
  // Caught by opening the year view in the running app on Josh's book: January
  // through April read "no activity" for a year he traded 356 times.
  const covered = coversWindow(trades, view === 'year' ? `${start.slice(0, 4)}-01-01` : start);
  // ⚠ The three cards below both views are whole-history claims. See their note.
  const wholeBook = !!trades && isWholeBook(trades);

  const byDate: CalendarByDate | null = useMemo(() => {
    if (!trades || !covered || !mode || !accounts) return null;
    // Safe: coverage was checked, so this cannot throw and the claim the header
    // makes ("July 2026") is one the loaded set can actually support.
    //
    // ⚠ SCOPED, and it was not before. Beta's calendar starts from
    // `tradesForActiveAccounts().filter(countsForMetrics)` like every other
    // analytics surface. Without it a SIM day shows a green cell on a calendar
    // whose totals no other screen agrees with.
    const rows = tradesCountingForMetrics(rowsForWindow(trades, start), selection, accounts);
    // ⚠ effectiveTradeDate, NOT t.date. In session mode a 22:36 trade belongs
    // to the NEXT session, and bucketing on the raw field silently opts this
    // whole screen out of the day-boundary feature. Resolved here because the
    // aggregator takes the date already decided.
    //
    // 🚨 THE SUM ITSELF MOVED TO `calendarDays` (#44). It was adding `netPnl`,
    // which is dollars whatever the switch says — so this tab could not follow
    // the unit at all, and changing only its formatters would have put a "pts"
    // suffix on dollar totals.
    return calendarDays(
      rows.map((t) => ({ ...t, effectiveDate: effectiveTradeDate(t, mode) })) as never,
      unit, contracts ?? [], journalled ?? null,
    );
  }, [trades, covered, start, mode, accounts, selection, journalled, unit, contracts]);

  /**
   * The caption on the captured picture: the period actually on screen.
   *
   * ⚠ NOT today's month. Every number in the region follows the arrows, so a
   * picture of May titled with July is a wrong label on a right picture — and
   * the label is the half a reader trusts without checking. Beta's
   * `_calendarPeriodLabel()` (app/index.html:29520).
   */
  const periodLabel = view === 'year' ? start.slice(0, 4) : monthLabel(start);

  /**
   * ⚠ REGISTERED ONLY ONCE THE REGION IS ACTUALLY ON THE PAGE. This tab returns
   * a bare "Loading…" until the boot read and the day-boundary mode land, and a
   * camera offered against a region that does not exist yet fails with
   * "region #calendar-snapshot-region is not on the page" — our internals, shown
   * to a user, for a button that should never have been pressable.
   *
   * ⚠ The effect sits ABOVE the early return because hooks must; the guard is
   * inside it rather than around it.
   */
  const { setTarget } = snapshot;
  const capturable = !(phase === 'booting' || !mode);
  useEffect(() => {
    if (!capturable) { setTarget(null); return; }
    setTarget({
      regionId: CALENDAR_SNAPSHOT_REGION_ID, title: periodLabel, label: 'the calendar',
    });
    return () => setTarget(null);
  }, [setTarget, capturable, periodLabel]);

  /**
   * ⚠ NO TRADE IDS — beta's `shareCalendar()` passes none
   * (app/index.html:29524). A shared calendar is the grid and the three cards
   * below it; the individual trades behind a cell are not published, and the
   * cells are not clickable on a static page.
   */
  const shareAccountLabel = activeAccountLabel(
    (accounts ?? []).map((a) => ({ id: String(a.id), name: a.name })),
    selection ?? [],
  );
  useEffect(() => {
    if (!capturable) { setSource(null); return; }
    setSource({
      kind: 'calendar', title: 'Calendar', subtitle: periodLabel,
      accountLabel: shareAccountLabel, tradeIds: [],
      unit, valueById: {},
    });
    return () => setSource(null);
  }, [setSource, capturable, periodLabel, shareAccountLabel, unit]);

  if (phase === 'booting' || !mode) return <p className="muted">Loading…</p>;

  const year = start.slice(0, 4);
  // ⚠ The tiles follow the VIEW. In year view they read the whole year, which is
  // beta's behaviour and the only thing that makes sense — four boxes labelled
  // "July" above twelve months would be a mismatch nobody would question.
  const kpiPrefix = view === 'year' ? year : monthPrefix(start);
  const kpiLabel = view === 'year' ? year : monthLabel(start);
  const kpis = byDate ? calendarKpis(byDate, kpiPrefix) : null;
  const rows = byDate ? monthGrid(byDate, start, today) : null;

  return (
    <div className="calendar-tab">
      {/* ⚠ ABOVE the tiles and right-aligned, as in beta — the toggle changes what
          every number below it means, so it reads before them. */}
      <div className="cal-head">
        <h2 className="cal-title">Calendar</h2>
        <div className="cal-view-toggle" role="tablist" aria-label="Calendar view">
          <button
            type="button" role="tab" aria-selected={view === 'month'}
            className={view === 'month' ? 'active' : ''}
            onClick={() => setView('month')}
          >Month</button>
          <button
            type="button" role="tab" aria-selected={view === 'year'}
            className={view === 'year' ? 'active' : ''}
            onClick={() => setView('year')}
          >Year</button>
        </div>
      </div>

      {/* ⚠ EVERYTHING BUT THE HEAD, which is beta's region exactly
          (app/index.html:30488-30531): the title and the Month/Year toggle stay
          out because they are chrome, and the toggle in particular is a control
          that does nothing in a picture.

          ⚠ Not re-indented, matching the Dashboard's region — the wrapper is a
          capture boundary rather than a layout box, and shifting 130 lines to
          say so would bury the change that matters. */}
      <div id={CALENDAR_SNAPSHOT_REGION_ID}>
        {/* Rendered only while the rasterizer is running, by the region that
            owns it. ⚠ "Performance Report", which is what beta's ONE title-bar
            builder writes on all three captures (app/index.html:15965) — the
            date beside it is what says which report this is. */}
        {snapshot.capturing === CALENDAR_SNAPSHOT_REGION_ID ? (
          <div className="snap-title-bar">
            <h1>{prefs?.displayName?.trim() ? `${prefs.displayName.trim()} ` : ''}Performance Report</h1>
            <span className="mono">{periodLabel}</span>
          </div>
        ) : null}

      {/* ⚠ The four tiles are BETA'S, in beta's order, and they are scoped to what
          is on screen — not to the global date filter. Paging to May and reading
          July's totals is the kind of quiet mismatch this tab is prone to, so the
          label carries the period. */}
      {kpis ? (
        <div className="cal-kpis" data-testid="cal-kpis">
          {/* ⚠ The P&L tile GLOWS by sign — beta's `glow-green` / `glow-red`. It
              is the one tile whose value can be good or bad, and the glow is how
              a period reads at a glance before any number is parsed. */}
          <Kpi
            label={`${kpiLabel} P&L`}
            value={unitValue(kpis.pnl, unit)}
            tone={pnlClass(kpis.pnl)}
            glow={kpis.pnl > 0 ? 'glow-green' : kpis.pnl < 0 ? 'glow-red' : ''}
            /* 🚨 THE NUMBER BESIDE THIS IS A SUM OF THINGS THAT DO NOT ADD (#67).
               In points or ticks, a month spanning ES and CL totals two different
               quantities of two different things. Beta prints a gold ⚠ here and
               the port did not — so the new client stated a meaningless figure
               with nothing on screen to flag it. In dollars it is a real total
               and no warning shows. */
            warn={
              (unit === 'points' || unit === 'ticks') && kpis.instruments > 1
                ? "Points/ticks don't sum across instruments"
                : null
            }
          />
          <Kpi label="Trades" value={String(kpis.trades)} />
          <Kpi label="Trading Days" value={String(kpis.tradingDays)} />
          <Kpi label="Win Days" value={String(kpis.winDays)} tone="green" />
        </div>
      ) : null}

      {view === 'year' && !covered ? (
        <div className="card cal-card">
          <NotLoaded
            what={year}
            phase={phase}
            loadWholeBook={loadWholeBook}
            why={`Only the last 45 days are loaded, so most of ${year} isn’t here. Twelve
              mini-calendars reading “no activity” would look like a year you barely
              traded — so the year isn’t drawn at all.`}
          />
        </div>
      ) : view === 'year' ? (
        <YearView unit={unit}
          byDate={byDate}
          year={year}
          onOpenDay={onOpenDay}
          onOpenMonth={(index) => {
            // ⚠ Stepping by MONTHS from the anchor, not setting a date: `offset`
            // is the only thing that moves this tab, so a month jump has to be
            // expressed in it or the two controls disagree.
            const cur = Number(start.slice(5, 7)) - 1;
            setOffset((o) => o + (index - cur));
            setView('month');
          }}
          // A year is twelve months of the same offset. Keeping ONE piece of
          // state for both views is what stops them drifting apart.
          onStepYear={(dir) => setOffset((o) => o + dir * 12)}
          onThisYear={() => setOffset(0)}
        />
      ) : (
      <div className="card cal-card">
        <div className="section-header cal-nav">
          <button type="button" className="btn" onClick={() => setOffset((o) => o - 1)}>‹ Prev</button>
          <button type="button" className="btn" onClick={() => setOffset(0)}>Today</button>
          <button type="button" className="btn" onClick={() => setOffset((o) => o + 1)}>Next ›</button>
          <h3 className="cal-month">{monthLabel(start)}</h3>
        </div>

        {!covered ? (
          // ⚠ NOT a grid of zeros. Thirty empty cells is a claim that the month
          // was quiet, and it is the single most believable wrong screen here.
          <NotLoaded
            what={monthLabel(start)}
            phase={phase}
            loadWholeBook={loadWholeBook}
            why={`Only the last 45 days are loaded, and this month falls outside it.
              An empty calendar here would look like a month you didn’t trade — so it
              isn’t drawn at all.`}
          />
        ) : (
          <div className="cal-grid" role="grid" aria-label={monthLabel(start)}>
            {DOW.map((d) => <div key={d} className="cal-dow" role="columnheader">{d}</div>)}
            <div className="cal-dow cal-dow-week" role="columnheader">Week</div>

            {(rows ?? []).map((row) => (
              <Row unit={unit} key={row.week.start} cells={row.cells} week={row.week} onOpenDay={onOpenDay} />
            ))}
          </div>
        )}
      </div>
      )}

      {/* ⚠ These three sit BELOW both views and read ALL history, not the month
          on screen — except the two "this month" rows in Streaks, which is why
          that card takes the anchor. Beta lays the first two side by side at
          1:2 and the heatmap full width beneath. */}
      {/* ── 🚨 THESE THREE NEED THE WHOLE BOOK, NOT A WINDOW ──
          "Longest win streak", the day-of-week averages and a heatmap labelled
          with a YEAR are all claims about ALL of history. Computed over the
          45-day boot read they come out UNDERSTATED and entirely plausible — a
          9-day streak reported as 3, a weekday's average drawn from four days —
          with nothing on screen to say the rest was never looked at.

          That is D47's whole argument, and the month grid above already refuses
          on the same grounds. So do these. One button loads the history and all
          three appear together. */}
      {!wholeBook ? (
        <div className="card cal-cards-gate">
          <NotLoaded
            what="Streaks, weekday averages and the heatmap"
            phase={phase}
            loadWholeBook={loadWholeBook}
            why={`All three read your ENTIRE history, and only the last 45 days are
              loaded. Computed on a slice they would be wrong in the flattering
              direction — a shorter losing streak than you actually had — so they
              aren’t drawn until the rest is here.`}
          />
        </div>
      ) : byDate ? (
        <>
          <div className="cal-cards">
            <StreaksCard unit={unit} byDate={byDate} monthIso={start} />
            <DowCard unit={unit} byDate={byDate} />
          </div>
          <HeatmapCard byDate={byDate} year={year} onOpenDay={onOpenDay} unit={unit} />
        </>
      ) : null}
      </div>{/* /calendar-snapshot-region */}

      {/* 🔴 THE CAMERA'S STATUS LINE, AND IT WAS ONLY ON THE DASHBOARD.
          snapshot.message carries "Snapshot opened in a new tab", the
          pop-up-blocked notice and every failure — and only the Dashboard
          rendered it. So on this tab a blocked pop-up or a failed raster said
          NOTHING AT ALL: the button un-greys and no picture ever appears, which
          reads as an app that ignored the click.

          ⚠ OUTSIDE the capture region, so the status never appears in the PNG. */}
      {snapshot.message ? (
        <p
          className={`muted snap-status ${snapshot.phase === 'failed' ? 'red' : ''}`}
          role={snapshot.phase === 'failed' ? 'alert' : 'status'}
        >
          {snapshot.message}
        </p>
      ) : null}

    </div>
  );
}

/**
 * ⚠ ONE COMPONENT FOR BOTH VIEWS. The month and the year fail the same way — a
 * grid of nothing that reads as a period the trader sat out — so they refuse in
 * the same words, and a future third view has somewhere to reuse.
 */
function NotLoaded(
  { what, why, phase, loadWholeBook }:
  { what: string; why: string; phase: string; loadWholeBook: () => void },
) {
  return (
    <>
      <h3>{what} isn’t loaded</h3>
      <p className="muted">{why}</p>
      <button
        type="button"
        className="btn primary"
        onClick={loadWholeBook}
        disabled={phase === 'backfilling'}
      >
        {phase === 'backfilling' ? 'Loading full history…' : 'Load full history'}
      </button>
    </>
  );
}

function Row(
  { cells, week, onOpenDay, unit }:
  { cells: readonly CalendarCell[]; week: WeekSummary; unit: PnlUnit; onOpenDay: ((d: string) => void) | undefined },
) {
  return (
    <>
      {cells.map((c) => <DayCell key={c.date} cell={c} onOpenDay={onOpenDay} unit={unit} />)}
      <WeekCell week={week} unit={unit} />
    </>
  );
}

function DayCell(
  { cell, onOpenDay, unit }:
  { cell: CalendarCell; onOpenDay: ((d: string) => void) | undefined; unit: PnlUnit },
) {
  const { day } = cell;
  const cls = ['cal-day'];
  if (!cell.inMonth) cls.push('outside');
  if (cell.isToday) cls.push('today');
  if (day) {
    if (day.pnl > 0) cls.push('win');
    if (day.pnl < 0) cls.push('loss');
    // Beta's $-calibrated emphasis for a day that actually mattered.
    // 🚨 $-CALIBRATED, SO IT ONLY APPLIES IN DOLLARS. Beta gates the same class
    // the same way. 250 POINTS on ES is a $12,500 day and 250 R is not a number
    // anybody has ever traded, so an ungated threshold would either emphasise
    // every cell or none of them — and the emphasis is meant to mark the days
    // worth looking at.
    // ⚠ THE CONSTANT, not a second literal 250. The domain declares it
    // (`HEAVY_DAY_USD`) and the year grid reads it; this line used to hardcode
    // the number, so the two views could have drifted apart silently — which is
    // exactly the failure they DID have, in the other half of this rule.
    if (unit === 'usd' && Math.abs(day.pnl) > HEAVY_DAY_USD) cls.push('heavy');
  }

  // ⚠ A day with NO trades is still clickable. Beta opens the Day Overview for
  // any cell, and a day you did not trade is a legitimate thing to look at —
  // the plan is still there, and so is the reason you sat out.
  const open = onOpenDay ? () => onOpenDay(cell.date) : undefined;

  return (
    <div
      className={cls.join(' ')}
      role="gridcell"
      data-day={cell.date}
      tabIndex={open ? 0 : undefined}
      onClick={open}
      onKeyDown={open ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
      } : undefined}
      aria-label={open ? `Open ${cell.date}` : undefined}
    >
      {day?.hasJournal || cell.isBest || cell.isWorst ? (
        <div className="cal-icons">
          {day?.hasJournal ? <span className="cal-ic journal" title="Journal entry">●</span> : null}
          {cell.isBest ? <span className="cal-ic best" title="Best day this month">★</span> : null}
          {cell.isWorst ? <span className="cal-ic worst" title="Worst day this month">▼</span> : null}
        </div>
      ) : null}
      <span className="cal-num">{cell.dayOfMonth}</span>
      {day ? (
        <>
          {/* ⚠ NO DECIMALS in a cell. Beta's `fmtPnlCompact` is `fmtPnlUnit(v,
              'usd', 0)` — "-$3,185", not "-$3,185.00". Two extra characters in a
              cell this narrow is the difference between a grid that fits and one
              that scrolls sideways. The tiles above keep full precision. */}
          <span className={`cal-pnl ${pnlClass(day.pnl)}`}>{unitCompact(day.pnl, unit)}</span>
          <span className="cal-n muted">{day.trades} {day.trades === 1 ? 'trade' : 'trades'}</span>
        </>
      ) : null}
    </div>
  );
}

function WeekCell({ week, unit }: { week: WeekSummary; unit: PnlUnit }) {
  const cls = ['cal-week'];
  if (week.isCurrentWeek) cls.push('current');
  if (!week.trades) cls.push('empty');
  return (
    <div className={cls.join(' ')} role="gridcell">
      <div className="cal-week-label">Week</div>
      {week.trades ? (
        <>
          <div className={`cal-week-pnl ${pnlClass(week.pnl)}`}>{unitCompact(week.pnl, unit)}</div>
          <div className="cal-week-meta">
            <span><b>{week.trades}</b>t</span>
            <span><b>{week.days}</b>d</span>
            {week.winPct != null ? <span><b>{week.winPct}%</b> w</span> : null}
          </div>
        </>
      ) : (
        <>
          <div className="cal-week-pnl none">—</div>
          <div className="cal-week-meta"><span>no trades</span></div>
        </>
      )}
    </div>
  );
}

/**
 * ⚠ THE SHIPPED TILE, not a new one. Beta and this client's own Dashboard both
 * use `.kpi` / `.kpi-label` / `.kpi-value`. The first version of this tab
 * invented `.cal-kpi*` and centred them, which is a second visual language for
 * the same object on a third tab — the drift the design-system relocation exists
 * to stop. Caught against beta's screenshot, not by anything mechanical.
 */
function Kpi(
  { label, value, tone, glow, warn }:
  { label: string; value: string; tone?: string; glow?: string; warn?: string | null },
) {
  return (
    <div className={`kpi ${glow ?? ''}`.trim()}>
      <div className="kpi-label">
        {label}
        {/* ⚠ `title`, so the reason is one hover away rather than absent. The
            glyph alone says "something is off" and not what — beta's tooltip is
            the whole explanation and it is one string. */}
        {warn ? <span className="cal-kpi-warn" title={warn}> ⚠</span> : null}
      </div>
      <div className={`kpi-value ${tone ?? ''}`.trim()}>{value}</div>
    </div>
  );
}

// ── Year view ────────────────────────────────────────────────────────────────

/**
 * Twelve mini-calendars, each on the SAME six-day grid as the month view.
 *
 * ⚠ Beta's on-screen note says so explicitly, and it matters: a year view laid
 * out over seven days would teach a different shape from the tab beside it.
 *
 * ⚠ The month NAME jumps to that month; a day CELL opens that day. Two targets
 * in one card, so the name stops the click from reaching the card.
 */
function YearView(
  { byDate, year, onOpenDay, onOpenMonth, onStepYear, onThisYear, unit }:
  {
    unit: PnlUnit;
    byDate: CalendarByDate | null;
    year: string;
    onOpenDay: ((d: string) => void) | undefined;
    onOpenMonth: (index: number) => void;
    onStepYear: (dir: -1 | 1) => void;
    onThisYear: () => void;
  },
) {
  const months = useMemo(
    () => (byDate ? yearMonths(byDate, year) : []),
    [byDate, year],
  );

  return (
    <div className="card cal-card">
      {/* ⚠ YEAR NAVIGATION, and the first version had NONE — just a static
          heading, so the year view could only ever show the year you happened to
          be in. Beta's buttons NAME the years ("‹ 2025", "2027 ›") rather than
          saying Prev/Next, which is worth keeping: it is the difference between
          knowing where you are going and finding out afterwards. */}
      <div className="section-header cal-nav">
        <button type="button" className="btn" onClick={() => onStepYear(-1)}>
          ‹ {Number(year) - 1}
        </button>
        <button type="button" className="btn" onClick={onThisYear}>This year</button>
        <button type="button" className="btn" onClick={() => onStepYear(1)}>
          {Number(year) + 1} ›
        </button>
        <h3 className="cal-month">{year}</h3>
      </div>
      <div className="cal-year-grid">
        {months.map((m) => (
          <div className="mini-month" key={m.month}>
            <div className="mini-header">
              <button
                type="button"
                className="mini-month-name"
                title={`View ${MONTH_NAMES[m.index]} ${year}`}
                // ⚠ The visible text is "Mar", which on its own says neither
                // which year nor that it navigates. The label carries both.
                aria-label={`View ${MONTH_NAMES[m.index]} ${year}`}
                onClick={() => onOpenMonth(m.index)}
              >
                {MONTH_SHORT[m.index]}
              </button>
              <span className={`mini-month-pnl ${pnlClass(m.pnl)}`}>
                {m.trades ? unitCompact(m.pnl, unit) : '—'}
              </span>
            </div>
            <div className="mini-dow">
              {['S', 'M', 'T', 'W', 'T', 'F'].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div className="mini-grid">
              {m.cells.map((c) => {
                const cls = ['mini-day'];
                if (!c.inMonth) cls.push('outside');
                if (!c.day) cls.push('no-data');
                if (c.tone) cls.push(c.tone);
                // 🔴 GATED ON DOLLARS, LIKE THE MONTH VIEW — and it was not,
                // which made this tab disagree with itself. `heavy` is a
                // $-calibrated fact the domain computes ($250+), so applying it
                // while the screen shows points, ticks or R emphasised cells
                // beta leaves pale. Same day, same threshold, two answers
                // depending which view you happened to be in.
                //
                // ⚠ The domain keeps computing it unconditionally and that is
                // right: "this day was big in dollars" is a fact about the day.
                // Whether to SHOW it is a display question, and it belongs here,
                // beside the identical line in `DayCell`.
                if (unit === 'usd' && c.heavy) cls.push('heavy');
                const open = c.day && onOpenDay ? () => onOpenDay(c.date) : undefined;
                return (
                  <div
                    key={c.date}
                    className={cls.join(' ')}
                    data-day={c.day ? c.date : undefined}
                    title={c.day ? `${c.date}: ${unitCompact(c.day.pnl, unit)} (${c.day.trades}t)` : c.date}
                    onClick={open}
                    tabIndex={open ? 0 : undefined}
                    onKeyDown={open ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
                    } : undefined}
                  >
                    <span className="mini-num">{c.dayOfMonth}</span>
                  </div>
                );
              })}
            </div>
            <div className="mini-footer">
              {m.trades ? (
                <>
                  <span>{m.trades} trades</span>
                  <span>{m.days} days</span>
                  {m.winPct != null ? <span>{m.winPct}% W</span> : null}
                </>
              ) : <span className="muted">no activity</span>}
            </div>
          </div>
        ))}
      </div>
      <p className="muted cal-hint">
        Every mini-calendar uses the same Sun–Fri layout as the Month view. Click a
        day to open it, or a month name to switch to that month.
      </p>
    </div>
  );
}

// ── Streaks & records ────────────────────────────────────────────────────────

/**
 * ⚠ TWO SCOPES IN ONE CARD, and the labels are the only thing that says so:
 * the three streak rows read ALL history, "best/worst day" reads THIS MONTH, and
 * "best month" reads this YEAR. That is beta's mix. It is defensible — a streak
 * is meaningless inside one month — but it is exactly the sort of thing that
 * gets "fixed" into consistency by someone who did not read the labels.
 */
function StreaksCard({ byDate, monthIso, unit }: { byDate: CalendarByDate; monthIso: string; unit: PnlUnit }) {
  const s = useMemo(() => streaks(byDate), [byDate]);
  const records = useMemo(() => monthRecords(byDate, monthIso), [byDate, monthIso]);
  const bestMonth = useMemo(
    () => bestMonthOfYear(byDate, monthIso.slice(0, 4)),
    [byDate, monthIso],
  );

  const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

  return (
    <div className="card cal-streaks">
      <h3><Icon name="analytics" /> Streaks &amp; records</h3>
      <Row2 label="Longest win streak" value={days(s.longestWin)} badge="win" />
      <Row2 label="Longest loss streak" value={days(s.longestLoss)} badge="loss" />
      <Row2
        label="Current streak"
        value={days(s.currentLen)}
        badge={s.currentDir === 1 ? 'win' : s.currentDir === -1 ? 'loss' : null}
      />
      <Row2
        label="Best day this month"
        value={records.best ? unitCompact(records.best.pnl, unit) : '—'}
        note={records.best ? dayLabelShort(records.best.date) : null}
      />
      <Row2
        label="Worst day this month"
        value={records.worst ? unitCompact(records.worst.pnl, unit) : '—'}
        note={records.worst ? dayLabelShort(records.worst.date) : null}
      />
      <Row2
        label="Best month YTD"
        value={bestMonth ? unitCompact(bestMonth.pnl, unit) : '—'}
        note={bestMonth ? MONTH_NAMES[Number(bestMonth.month.slice(5, 7)) - 1] ?? null : null}
      />
    </div>
  );
}

function Row2(
  { label, value, badge, note }:
  { label: string; value: string; badge?: 'win' | 'loss' | null; note?: string | null },
) {
  return (
    <div className="streak-row">
      <span className="streak-label">{label}</span>
      <span className="streak-val">
        {value}
        {note ? <span className="streak-note">{note}</span> : null}
        {badge ? <span className={`streak-badge ${badge}`}>{badge === 'win' ? 'W' : 'L'}</span> : null}
      </span>
    </div>
  );
}

// ── Day-of-week breakdown ────────────────────────────────────────────────────

/**
 * ⚠ THE BAR GROWS FROM THE CENTRE, both ways. It is anchored at 50% and a losing
 * weekday's bar is translated left, so the column reads as a diverging chart
 * around zero rather than five bars of different lengths. Beta's, and it is the
 * only thing that makes a mixed week legible at a glance.
 */
function DowCard({ byDate, unit }: { byDate: CalendarByDate; unit: PnlUnit }) {
  const buckets = useMemo(() => dowBreakdown(byDate), [byDate]);

  return (
    <div className="card">
      <h3><Icon name="analytics" /> Day-of-week breakdown</h3>
      <div className="dow-table">
        <div className="dow-head">Day</div>
        <div className="dow-head">Trades</div>
        <div className="dow-head dow-c">Distribution</div>
        <div className="dow-head dow-r">Win %</div>
        <div className="dow-head dow-r">Avg P&amp;L</div>

        {buckets.map((b) => (
          <Fragment key={b.name}>
            <div className="dow-row dow-name">
              {b.name}
              {b.strongest ? <span className="dow-badge strong">strongest</span> : null}
              {b.weakest ? <span className="dow-badge weak">weakest</span> : null}
            </div>
            <div className="dow-row dow-mono">{b.trades}</div>
            <div className="dow-row dow-bar-cell">
              <div className="dow-bar">
                <div
                  className={b.avg >= 0 ? 'green' : 'red'}
                  style={{
                    width: `${dowBarPct(buckets, b.avg)}%`,
                    transform: b.avg < 0 ? 'translateX(-100%)' : 'none',
                  }}
                />
              </div>
            </div>
            <div className="dow-row dow-wr">{b.days ? `${b.winPct}%` : '—'}</div>
            <div className={`dow-row dow-pnl ${pnlClass(b.avg)}`}>
              {b.days ? unitCompact(b.avg, unit) : '—'}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// ── Year heatmap ─────────────────────────────────────────────────────────────

function HeatmapCard(
  { byDate, year, onOpenDay, unit }:
  {
    byDate: CalendarByDate; year: string;
    onOpenDay: ((d: string) => void) | undefined; unit: PnlUnit;
  },
) {
  const map = useMemo(() => yearHeatmap(byDate, year), [byDate, year]);

  return (
    <div className="card cal-heat-card">
      <h3><Icon name="analytics" /> {year} daily P&amp;L heatmap</h3>
      <div className="cal-heat">
        {/* Sun and Sat are unlabelled — beta labels only the three that read. */}
        <div className="heat-rows"><span>Mon</span><span>Wed</span><span>Fri</span></div>
        <div>
          <div
            className="heat-months"
            style={{ gridTemplateColumns: `repeat(${map.weeks}, 15px)` }}
          >
            {map.monthLabels.map((m) => (
              <span key={m.week} style={{ gridColumn: m.week + 1 }}>{MONTH_SHORT[m.month]}</span>
            ))}
          </div>
          <div className="heat-scroll">
            <div className="heat-grid">
              {map.cells.map((c) => {
                const cls = ['heat-cell'];
                if (c.level) cls.push(`l${c.level}`, c.tone);
                const open = c.day && onOpenDay ? () => onOpenDay(c.date) : undefined;
                return (
                  <div
                    key={c.date}
                    className={cls.join(' ')}
                    data-day={c.day ? c.date : undefined}
                    title={c.day ? `${c.date}: ${unitCompact(c.day.pnl, unit)} (${c.day.trades}t)` : c.date}
                    onClick={open}
                  />
                );
              })}
            </div>
          </div>
          <div className="heat-legend">
            <span>Loss</span>
            <span className="swatch" style={{ background: 'rgba(246,104,94,1)' }} />
            <span className="swatch" style={{ background: 'rgba(246,104,94,0.45)' }} />
            <span className="swatch" style={{ background: 'var(--bg-3)' }} />
            <span className="swatch" style={{ background: 'rgba(46,157,255,0.45)' }} />
            <span className="swatch" style={{ background: 'rgba(46,157,255,1)' }} />
            <span>Win</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "Jul 14" — the short stamp beside a record. */
function dayLabelShort(iso: string): string {
  return `${MONTH_SHORT[Number(iso.slice(5, 7)) - 1] ?? ''} ${Number(iso.slice(8))}`;
}
